import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { ModalProvider } from "styled-react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faCircle,
  faLock
} from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { CloseButton, IconButton } from "../styled-components/";
import {
  Container,
  StickyHeader,
  MainContent
} from "../styled-components/Layout";
import { actions as appActions } from "../actions/app";
import { getAxisUserProfile } from "../actions/auth";
import { actions as infoActions } from "../actions/info";
import localize from "../util/localizeUtils";
import LicenseDialog from "./LicenseDialog";
import { Modal, ModalOverlay, ModalHeader } from "../styled-components/Modal";

const Mousetrap = window.Mousetrap;

const ComponentContainer = styled.div`
  display: inline-block;
`;

const Divider = styled.hr`
  margin: 0.1rem 1rem;
  max-width: 100%;
  height: 0;
  border-right: 0;
  border-top: 0;
  border-bottom: 1px solid ${props => props.theme.border};
  border-left: 0;
  clear: both;
`;

const StatusIcon = styled.div`
  color: ${props =>
    props.connected ? props.theme.colors.green : props.theme.colors.red};
  margin-right: 0.25rem;
`;
const LockIcon = styled.div`
  margin-right: 0.25rem;
`;

const InfoBody = styled(MainContent)`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  z-index: 1000;
  background-color: ${props => props.theme.modal.background};
`;

const Group = styled.div`
  display: flex;
  padding: 0 1rem;
`;

const StatusColumn = styled.div`
  flex: 1;
  padding: 0.25rem;

  text-align: ${props => (props.header ? "right" : "left")};
  color: ${props =>
    props.header ? props.theme.text.secondary : props.theme.text.primary};
`;

const StatusItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;

  > span {
    max-width: 9rem;
    display: flex;
    align-items: center;
  }
`;

const StatusLineItem = styled.div`
  display: flex;
  align-items: center;
`;

const LOADING_IMAGE = "images/inputLoading.gif";

const LoadingIcon = styled.img`
  height: 0.85rem;
  width: 0.85rem;
  margin-left: 0.25rem;
`;

const ChyronCopyright = styled.div`
  color: ${props => props.theme.text.secondary};
`;

class InfoDialog extends Component {
  constructor() {
    super();

    this.state = {
      open: false
    };
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.open &&
      !nextProps.connectionsLoaded &&
      !nextProps.connectionStatusLoading
    ) {
      nextProps.checkConnectionStatus();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const mediaEngineChanged =
      this.props.mediaEngine.ip !== nextProps.mediaEngine.ip &&
      this.props.mediaEngine.connected !== nextProps.mediaEngine.connected &&
      this.props.mediaEngine.licensed !== nextProps.mediaEngine.licensed;
    const publishingChanged =
      this.props.publishing.ip !== nextProps.publishing.ip &&
      this.props.publishing.connected !== nextProps.publishing.connected &&
      this.props.publishing.licensed !== nextProps.publishing.licensed;

    const propsChanged = Object.keys(this.props).some(
      key => this.props[key] !== nextProps[key]
    );
    const stateChanged = Object.keys(this.state).some(
      key => this.state[key] !== nextState[key]
    );

    return (
      propsChanged || stateChanged || mediaEngineChanged || publishingChanged
    );
  }

  getServerStatusIcon = (serverStatus, licensed) => {
    if (serverStatus) {
      const connectedText = localize("connectedLabel");
      const licenseText =
        licensed !== undefined ? this.getLicenseStatus(licensed) : "";
      return (
        <StatusLineItem>
          <StatusIcon connected>
            <FontAwesomeIcon fixedWidth size="sm" icon={faCircle} />
          </StatusIcon>
          {licensed !== undefined && licensed && (
            <LockIcon>
              <FontAwesomeIcon fixedWidth size="sm" icon={faLock} />
            </LockIcon>
          )}
          {connectedText} {licenseText}
        </StatusLineItem>
      );
    }
    return (
      <StatusLineItem>
        <StatusIcon>
          <FontAwesomeIcon fixedWidth size="sm" icon={faCircle} />
        </StatusIcon>
        {localize("disconnectedLabel")}
      </StatusLineItem>
    );
  };

  getLicenseStatus = licensed =>
    licensed ? localize("licensedLabel") : localize("unlicensedLabel");

  openDialog = () => {
    this.props.getLuciInformation();
    if (!this.props.connectionsLoaded) {
      this.props.checkConnectionStatus();
    }
    if (!this.props.axisAuthenticated) {
      this.props.getAxisUserProfile();
    }

    this.setState({ open: true });
    Mousetrap.bindGlobal("esc", this.closeDialog);
  };

  closeDialog = () => {
    this.setState({ open: false });
    Mousetrap.unbind("esc");
  };

  render() {
    const {
      username,
      licensedOptions,
      axisServices,
      luciVersion,
      camioServer,
      camioVersion,
      serverStatus,
      chyronCopyright,
      loading,
      axisServicesLoaded,
      axisServicesLoading,
      axisAuthenticated,
      mediaEngine,
      publishing,
      connectionStatusLoading,
      connectionsLoaded
    } = this.props;

    return (
      <ComponentContainer>
        <IconButton className="info-modal-open" onClick={this.openDialog}>
          <FontAwesomeIcon fixedWidth size="xs" icon={faInfoCircle} />
        </IconButton>
        <ModalProvider>
          <Modal
            isOpen={this.state.open}
            onRequestClose={this.closeDialog}
            contentLabel="InfoModal"
          >
            <Container>
              <ModalHeader>
                <span>{localize("infoDialogTitle")}</span>
                <CloseButton onClick={() => this.setState({ open: false })}>
                  &times;
                </CloseButton>
              </ModalHeader>
              <InfoBody>
                <Group>
                  <StatusColumn header>
                    <div>{localize("luciVersionLabel")}</div>
                    <div>{localize("usernameLabel")}</div>
                  </StatusColumn>
                  <StatusColumn>
                    <div>{luciVersion}</div>
                    <div>
                      {!loading ? (
                        username
                      ) : (
                        <LoadingIcon src={LOADING_IMAGE} />
                      )}
                    </div>
                  </StatusColumn>
                </Group>
                <Divider />
                <Group>
                  <StatusColumn header>
                    {localize("licensedOptionsLabel")}
                  </StatusColumn>
                  <StatusColumn>
                    {!loading ? (
                      licensedOptions
                    ) : (
                      <LoadingIcon src={LOADING_IMAGE} />
                    )}
                  </StatusColumn>
                </Group>
                <Divider />
                <Group>
                  <StatusColumn header>
                    <div>{localize("axisStatusLabel")}</div>
                    <div>{localize("axisServicesLabel")}</div>
                  </StatusColumn>
                  <StatusColumn>
                    <StatusItemContainer>
                      {axisServicesLoaded && !axisServicesLoading ? (
                        this.getServerStatusIcon(axisAuthenticated)
                      ) : (
                        <span>
                          <LoadingIcon src={LOADING_IMAGE} />
                        </span>
                      )}
                    </StatusItemContainer>
                    <div>
                      {axisServicesLoaded && !axisServicesLoading ? (
                        axisServices
                      ) : (
                        <LoadingIcon src={LOADING_IMAGE} />
                      )}
                    </div>
                  </StatusColumn>
                </Group>
                <Divider />
                <Group>
                  <StatusColumn header>
                    <div>{localize("camioVersionLabel")}</div>
                    <div>{localize("camioServerLabel")}</div>
                    <div>{localize("camioStatusLabel")}</div>
                  </StatusColumn>
                  <StatusColumn>
                    <div>
                      {!loading ? (
                        camioVersion
                      ) : (
                        <LoadingIcon src={LOADING_IMAGE} />
                      )}
                    </div>
                    <div>
                      {!loading ? (
                        camioServer
                      ) : (
                        <LoadingIcon src={LOADING_IMAGE} />
                      )}
                    </div>
                    <StatusItemContainer>
                      {!loading ? (
                        this.getServerStatusIcon(serverStatus)
                      ) : (
                        <span>
                          <LoadingIcon src={LOADING_IMAGE} />
                        </span>
                      )}
                    </StatusItemContainer>
                  </StatusColumn>
                </Group>
                <Divider />
                <Group>
                  <StatusColumn header>
                    <div>{localize("mediaEngineServerLabel")}</div>
                    <div>{localize("mediaEngineStatusLabel")}</div>
                  </StatusColumn>
                  <StatusColumn>
                    <div>
                      {connectionsLoaded &&
                      (!mediaEngine.loading || connectionStatusLoading) ? (
                        mediaEngine.ip || "Not Configured"
                      ) : (
                        <LoadingIcon src={LOADING_IMAGE} />
                      )}
                    </div>
                    <StatusItemContainer>
                      {connectionsLoaded &&
                      (!mediaEngine.loading || connectionStatusLoading) ? (
                        this.getServerStatusIcon(
                          mediaEngine.connected,
                          mediaEngine.licensed
                        )
                      ) : (
                        <span>
                          <LoadingIcon src={LOADING_IMAGE} />
                        </span>
                      )}
                    </StatusItemContainer>
                  </StatusColumn>
                </Group>
                <Divider />
                <Group>
                  <StatusColumn header>
                    <div>{localize("publishingServerLabel")}</div>
                    <div>{localize("publishingStatusLabel")}</div>
                  </StatusColumn>
                  <StatusColumn>
                    <div>
                      {connectionsLoaded &&
                      (!publishing.loading || connectionStatusLoading) ? (
                        publishing.ip || "Not Configured"
                      ) : (
                        <LoadingIcon src={LOADING_IMAGE} />
                      )}
                    </div>
                    <StatusItemContainer>
                      {connectionsLoaded &&
                      (!publishing.loading || connectionStatusLoading) ? (
                        this.getServerStatusIcon(
                          publishing.connected,
                          publishing.licensed
                        )
                      ) : (
                        <span>
                          <LoadingIcon src={LOADING_IMAGE} />
                        </span>
                      )}
                    </StatusItemContainer>
                  </StatusColumn>
                </Group>
                <Divider />
                <Group>
                  <StatusColumn>
                    <LicenseDialog />
                  </StatusColumn>
                  <StatusColumn>
                    <ChyronCopyright>{chyronCopyright}</ChyronCopyright>
                  </StatusColumn>
                </Group>
              </InfoBody>
            </Container>
          </Modal>
        </ModalProvider>
      </ComponentContainer>
    );
  }
}

InfoDialog.defaultProps = {
  username: null
};

InfoDialog.propTypes = {
  username: PropTypes.string,
  licensedOptions: PropTypes.string.isRequired,
  axisServices: PropTypes.string.isRequired,
  luciVersion: PropTypes.string.isRequired,
  camioServer: PropTypes.string.isRequired,
  camioVersion: PropTypes.string.isRequired,
  serverStatus: PropTypes.bool.isRequired,
  chyronCopyright: PropTypes.string.isRequired,
  getLuciInformation: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  axisServicesLoaded: PropTypes.bool.isRequired,
  axisServicesLoading: PropTypes.bool.isRequired,
  axisAuthenticated: PropTypes.bool.isRequired,
  mediaEngine: PropTypes.shape({
    ip: PropTypes.string,
    licensed: PropTypes.bool,
    connected: PropTypes.bool
  }).isRequired,
  publishing: PropTypes.shape({
    ip: PropTypes.string,
    licensed: PropTypes.bool,
    connected: PropTypes.bool
  }).isRequired,
  connectionStatusLoading: PropTypes.bool.isRequired,
  connectionsLoaded: PropTypes.bool.isRequired,
  checkConnectionStatus: PropTypes.func.isRequired,
  getAxisUserProfile: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  username: state.info.username,
  licensedOptions: state.info.licensedOptions,
  axisServices: state.info.axisServices,
  luciVersion: state.info.luciVersion,
  camioServer: state.info.camioServer,
  camioVersion: state.info.camioVersion,
  serverStatus: state.info.serverStatus,
  chyronCopyright: state.info.chyronCopyright,
  loading: state.info.loading,
  axisServicesLoaded: state.auth.axisUserProfile.loaded,
  axisServicesLoading: state.auth.axisUserProfile.loading,
  axisAuthenticated:
    state.auth.user && state.auth.axisUserProfile.services.length > 0,
  mediaEngine: state.app.connections.mediaEngine || {},
  publishing: state.app.connections.publishing || {},
  connectionStatusLoading: state.app.connectionStatusLoading,
  connectionsLoaded: state.app.connections.mediaEngine.connected !== undefined
});

const mapActionCreators = {
  getAxisUserProfile,
  getLuciInformation: infoActions.getLuciInformation,
  checkConnectionStatus: appActions.checkConnectionStatus
};

export default connect(mapStateToProps, mapActionCreators)(InfoDialog);



// WEBPACK FOOTER //
// ./app/components/InfoDialog.js